export const Asset = {
  Field: 'field',
  Dataset: 'dataset',
  DatasetGroup: 'dataset_group',
  Datasource: 'datasource',
  System: 'system',
  Attribute: 'attribute',
  AttributeValue: 'attribute_value',
  Entity: 'entity',
  Glossary: 'glossary',
  User: 'user',
  UserGroup: 'user_group',
  AttributeUnit: 'attribute_unit',
  Task: 'task',
  Job: 'job',
};

export const AssetName = {
  field: 'Field',
  dataset: 'Dataset',
  dataset_group: 'Dataset Group',
  datasource: 'Datasource',
  system: 'System',
  attribute: 'Attribute',
  attribute_value: 'Attribute Value',
  entity: 'Entity',
  glossary: 'Glossary',
  user: 'User',
  user_group: 'User Group',
  attribute_unit: 'Attribute Unit',
  task: 'Task',
  job: 'Job',
};

export const AssetNamePlural = {
  field: 'Fields',
  dataset: 'Datasets',
  dataset_group: 'Dataset Groups',
  datasource: 'Datasources',
  system: 'Systems',
  attribute: 'Attributes',
  attribute_value: 'Attribute Values',
  entity: 'Entities',
  glossary: 'Glossaries',
  user: 'Users',
  user_group: 'User Groups',
  attribute_unit: 'Attribute Units',
  task: 'Tasks',
  job: 'Jobs',
};

export const authMethod = {
  oidc: 'Microsoft Entra ID',
  local: 'Local',
};

export const Role = {
  admin: 'Admin',
  editor: 'Editor',
  viewer: 'Viewer',
};

/**
 * The attribute roles seeded in the `attribute_role` table, by name. Role checks compare against the
 * `attribute_role_name` on the row rather than against `attribute_role_id`, which is a surrogate key
 * and carries no guarantee of being the same number in every installation.
 */
export const AttributeRole = {
  dimension: 'Dimension',
  measure: 'Measure',
};

export const Operation = {
  D: { label: 'Delete', className: 'pill pill-red' },
  I: { label: 'Insert', className: 'pill pill-green' },
  U: { label: 'Update', className: 'pill pill-blue' },
  // Not a database operation: the API reports an insert that moved an existing asset between the Entity
  // and Attribute tables as a conversion, since it is a change of kind rather than a creation.
  C: { label: 'Convert', className: 'pill pill-blue' },
};

export const TaskType = {
  adsync: 'adsync',
  dssync: 'dssync',
  rebuildsearchindex: 'rebuildsearchindex',
};

export const keyCode = {
  SPACE: 32,
  ENTER: 13,
  DOWN_ARROW: 40,
  UP_ARROW: 38,
  LEFT_ARROW: 37,
  RIGHT_ARROW: 39,
  ESCAPE: 27,
  TAB: 9,
  A: 65,
  F: 70,
  K: 75,
};

export const AttributeSensitivity = {
  1: { className: 'pill pill-wide pill-full-red' },
  2: { className: 'pill pill-wide pill-semifull-red' },
  3: { className: 'pill pill-wide pill-red' },
  4: { className: 'pill pill-wide pill-dark-grey' },
  5: { className: 'pill pill-wide' },
  6: { className: 'pill pill-wide pill-grey' },
};

export const DatasetSubtypeOperation = {
  select: { className: 'pill pill-blue' },
  insert: { className: 'pill pill-green' },
  update: { className: 'pill pill-yellow' },
  delete: { className: 'pill pill-red' },
  other: { className: 'pill pill-grey' },
};

export const Status = {
  1: { name: 'Development', className: 'pill pill-blue' },
  2: { name: 'Test', className: 'pill pill-yellow' },
  3: { name: 'Production', className: 'pill' },
  4: { name: 'Endorsed', className: 'pill pill-purple' },
  5: { name: 'Deprecated', className: 'pill pill-red' },
};

export const DatatypeCategory = [
  { value: 'array', label: 'Array', icon: 'array' },
  { value: 'binary', label: 'Binary', icon: 'binary' },
  { value: 'boolean', label: 'Boolean', icon: 'boolean' },
  { value: 'date', label: 'Date', icon: 'date' },
  { value: 'datetime', label: 'Datetime', icon: 'datetime' },
  { value: 'decimal', label: 'Decimal', icon: 'decimal' },
  { value: 'geography', label: 'Geography', icon: 'geography' },
  { value: 'integer', label: 'Integer', icon: 'integer' },
  { value: 'object', label: 'Object', icon: 'object' },
  { value: 'string', label: 'String', icon: 'string' },
  { value: 'tag', label: 'Tag', icon: 'tag' },
  { value: 'time', label: 'Time', icon: 'time' },
  { value: 'unknown', label: 'Unknown', icon: 'unknown' },
];

export const environments = [
  { value: 'prod', environment: 'prod', label: 'Production' },
  { value: 'dev', environment: 'dev', label: 'Development' },
  { value: 'at', environment: 'at', label: 'Acceptance Testing' },
  { value: 'st', environment: 'st', label: 'System Testing' },
  { value: 'test', environment: 'test', label: 'Testing' },
  { value: 'train', environment: 'train', label: 'Training' },
  { value: 'uat', environment: 'uat', label: 'User Acceptance Testing' },
  { value: 'stg', environment: 'stg', label: 'Staging' },
  { value: 'qa', environment: 'qa', label: 'Quality Assurance' },
  { value: 'int', environment: 'int', label: 'Integration' },
  { value: 'demo', environment: 'demo', label: 'Demonstration' },
];

export const CUSTOM_PROPERTY_OBJECT_MAP = [
  { value: 'glossary', label: 'Glossary' },
  { value: 'entity', label: 'Entity' },
  { value: 'attribute', label: 'Attribute' },
  { value: 'attribute_value', label: 'Attribute Value' },
  { value: 'system', label: 'System' },
  { value: 'datasource', label: 'Datasource' },
  { value: 'dataset_group', label: 'Dataset Group' },
  { value: 'dataset', label: 'Dataset' },
  { value: 'field', label: 'Field' },
];

export const COLOR_THEME = {
  LIGHT: 'light',
  DARK: 'dark',
  AUTO: 'auto',
};

export const ASSET_HIERARCHY_MAP = {
  system: 'system',
  datasource: 'system',
  'dataset group': 'system',
  dataset: 'system',
  field: 'system',
  glossary: 'glossary',
  entity: 'glossary',
  attribute: 'glossary',
  'attribute value': 'glossary',
};

const isMacOS = () => {
  const platform = (navigator.platform || '').toLowerCase();
  const ua = (navigator.userAgent || '').toLowerCase();
  // Mac platforms or Mac user-agent; also treat iPadOS as non-Mac for keyboard shortcuts
  return platform.includes('mac') || ua.includes('macintosh') || ua.includes('mac os x');
};

const modKeyLabel = isMacOS() ? '⌘' : 'Ctrl';

export const KEYBOARD_SHORTCUT = {
  search: `${modKeyLabel} K`,
  filter: `${modKeyLabel} F`,
};
